import * as React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, MousePointer2, PartyPopper, Pause, Play } from "lucide-react";
import { TUTORIAL_MODULES, sceneIcon, type TutorialModule } from "./learnAppData";
import { TutorialPageMock } from "./TutorialPageMock";

const DEFAULT_SCENE_SEC = 5;

function sceneDurationMs(durationSec?: number) {
  return (durationSec ?? DEFAULT_SCENE_SEC) * 1000;
}

/** Faithful port of vawena-frontend-webapp's FlowTutorials.tsx "Learn the app" tour. */
export const PlatformTour: React.FC = () => {
  const [activeModuleId, setActiveModuleId] = React.useState(TUTORIAL_MODULES[0]?.id ?? "");
  const activeModule = React.useMemo(
    () => TUTORIAL_MODULES.find((m) => m.id === activeModuleId) ?? TUTORIAL_MODULES[0],
    [activeModuleId]
  );

  const [sceneIndex, setSceneIndex] = React.useState(0);
  const [playing, setPlaying] = React.useState(true);
  const [finished, setFinished] = React.useState(false);
  const rafRef = React.useRef<number | null>(null);
  const startedAtRef = React.useRef(0);
  const elapsedRef = React.useRef(0);

  const scenes = activeModule?.scenes ?? [];
  const scene = scenes[sceneIndex];
  const total = scenes.length;
  const ModuleIcon = activeModule?.icon;
  const SceneIcon = scene ? sceneIcon(scene.navLabel) : undefined;

  const tabsScrollRef = React.useRef<HTMLDivElement | null>(null);
  const selectedTabRef = React.useRef<HTMLButtonElement | null>(null);

  React.useEffect(() => {
    const container = tabsScrollRef.current;
    const tab = selectedTabRef.current;
    if (!container || !tab) return;
    // Manual, contained scroll — never let this touch any ancestor outside the tabs row.
    const targetLeft = tab.offsetLeft - container.clientWidth / 2 + tab.offsetWidth / 2;
    container.scrollTo({ left: Math.max(0, targetLeft), behavior: "smooth" });
  }, [activeModuleId]);

  /* Scale the desktop-sized player 1:1 to fit any viewport — same layout as PC/iPad, just scaled. */
  const DESKTOP_PLAYER_WIDTH = 900;
  const scaleOuterRef = React.useRef<HTMLDivElement | null>(null);
  const scaleInnerRef = React.useRef<HTMLDivElement | null>(null);
  const [playerScale, setPlayerScale] = React.useState(1);
  const [playerHeight, setPlayerHeight] = React.useState(0);

  React.useLayoutEffect(() => {
    const update = () => {
      if (!scaleOuterRef.current || !scaleInnerRef.current) return;
      const outerWidth = scaleOuterRef.current.offsetWidth;
      const s = outerWidth / DESKTOP_PLAYER_WIDTH;
      setPlayerScale(s);
      setPlayerHeight(scaleInnerRef.current.offsetHeight * s);
    };
    update();
    const ro = new ResizeObserver(update);
    if (scaleOuterRef.current) ro.observe(scaleOuterRef.current);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeModuleId, sceneIndex, finished]);

  const stopRaf = () => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  };

  const goToScene = React.useCallback((index: number) => {
    stopRaf();
    elapsedRef.current = 0;
    setSceneIndex(index);
    setFinished(false);
  }, []);

  const startModule = (m: TutorialModule) => {
    stopRaf();
    setActiveModuleId(m.id);
    setSceneIndex(0);
    elapsedRef.current = 0;
    setFinished(false);
    setPlaying(true);
  };

  React.useEffect(() => {
    if (!playing || finished || !scene) {
      stopRaf();
      return;
    }
    const duration = sceneDurationMs(scene.durationSec);
    startedAtRef.current = performance.now() - elapsedRef.current;

    const tick = (now: number) => {
      const elapsed = now - startedAtRef.current;
      elapsedRef.current = elapsed;
      const p = Math.min(1, elapsed / duration);
      if (p >= 1) {
        elapsedRef.current = 0;
        if (sceneIndex >= total - 1) {
          setPlaying(false);
          setFinished(true);
          return;
        }
        setSceneIndex((i) => i + 1);
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return stopRaf;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, finished, scene, sceneIndex, total]);

  return (
    <div className="flex flex-col w-full min-w-0 dark:bg-vw-surface bg-white rounded-2xl border dark:border-white/[0.08] border-gray-100 shadow-2xl dark:shadow-blue-900/30 overflow-hidden">
      {/* Header */}
      <div className="w-full border-b dark:border-white/[0.08] border-gray-100 px-5 py-3">
        <div className="flex justify-between items-center gap-3 mb-3">
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-bold dark:text-white text-gray-900">Learn the app</h2>
            <p className="text-sm dark:text-slate-400 text-gray-500">Start with Get Started, then tour each module.</p>
          </div>
          {playing && !finished ? (
            <button
              type="button"
              onClick={() => setPlaying(false)}
              className="inline-flex items-center gap-1.5 shrink-0 text-sm font-semibold text-blue-600 border border-blue-600/20 rounded-md px-2.5 py-1.5 hover:bg-blue-600/5"
            >
              <Pause size={12} fill="currentColor" />
              Pause
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                if (finished) goToScene(0);
                setPlaying(true);
                setFinished(false);
              }}
              className="inline-flex items-center gap-1.5 shrink-0 text-sm font-semibold dark:text-slate-400 text-gray-600 border dark:border-white/10 border-gray-200 rounded-md px-2.5 py-1.5 hover:border-blue-600 hover:text-blue-600"
            >
              <Play size={12} className="ml-0.5" fill="currentColor" />
              {finished ? "Replay" : "Play"}
            </button>
          )}
        </div>

        {/* Top-level module tabs */}
        <div className="relative -mx-0.5">
          <div ref={tabsScrollRef} className="flex gap-1.5 overflow-x-auto no-scrollbar px-0.5">
            {TUTORIAL_MODULES.map((m) => {
              const Icon = m.icon;
              const selected = m.id === activeModuleId;
              return (
                <button
                  key={m.id}
                  ref={selected ? selectedTabRef : undefined}
                  type="button"
                  onClick={() => startModule(m)}
                  className={`inline-flex items-center gap-1.5 shrink-0 whitespace-nowrap rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
                    selected
                      ? "bg-blue-600 text-white border-blue-600"
                      : "dark:bg-vw-surface bg-white dark:text-slate-400 text-gray-600 dark:border-white/10 border-gray-200 hover:border-gray-300 dark:hover:text-white hover:text-gray-900"
                  }`}
                >
                  <Icon className="shrink-0 w-3.5 h-3.5" />
                  <span>{m.name}</span>
                </button>
              );
            })}
          </div>
          <div className="pointer-events-none absolute inset-y-0 left-0 w-6 bg-gradient-to-r dark:from-vw-surface from-white to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l dark:from-vw-surface from-white to-transparent" />
        </div>
      </div>

      {/* Player */}
      <div className="grid w-full items-start grid-cols-1 lg:grid-cols-[1.35fr_1fr] gap-5 p-3 sm:p-5">
        <div className="w-full min-w-0 flex flex-col gap-2">
          {/* Browser mock — always rendered at desktop scale, then scaled down to fit; identical layout on every device */}
          <div ref={scaleOuterRef} className="w-full overflow-hidden" style={{ height: playerHeight || undefined }}>
          <div
            ref={scaleInnerRef}
            style={{ width: DESKTOP_PLAYER_WIDTH, transform: `scale(${playerScale})`, transformOrigin: "top left" }}
          >
          <div className="rounded-lg border dark:border-white/10 border-gray-300 bg-white shadow-lg relative overflow-hidden" style={{ width: DESKTOP_PLAYER_WIDTH }}>
            <div className="w-full aspect-[16/10] flex flex-col">
            <div className="shrink-0 flex items-center gap-2 px-2.5 py-1.5 border-b border-gray-200 bg-gray-50">
              <div className="flex gap-1.5 shrink-0">
                <span className="w-2 h-2 rounded-full bg-red-400" />
                <span className="w-2 h-2 rounded-full bg-amber-400" />
                <span className="w-2 h-2 rounded-full bg-emerald-400" />
              </div>
              <span className="text-xs text-gray-900 truncate font-mono min-w-0">
                app.vawena.ai{scene?.path || ""}
              </span>
            </div>

            <div className="flex flex-1 min-h-0 relative">
              {/* Fake sidebar — real scene nav for the active module, clickable */}
              <aside className="flex w-[148px] shrink-0 flex-col border-r border-blue-200 bg-white overflow-hidden">
                <div className="shrink-0 flex items-center gap-1.5 px-2.5 py-2 border-b border-blue-200">
                  <div className="h-5 w-5 rounded bg-blue-600 flex items-center justify-center shrink-0">
                    <span className="text-white text-[9px] font-bold">V</span>
                  </div>
                  <span className="text-xs font-semibold text-gray-900">VaWeNa</span>
                </div>

                <nav className="flex-1 min-h-0 overflow-y-auto py-1.5 px-1.5 no-scrollbar">
                  {activeModule?.sidebarTitle && activeModule.sidebarTitle !== "VaWeNa" && (
                    <div className="flex items-center gap-1.5 px-2 py-1.5 mb-0.5 rounded-md border border-blue-400/40 bg-blue-50 text-blue-600">
                      {ModuleIcon && <ModuleIcon className="shrink-0 w-4 h-4" />}
                      <span className="text-xs font-semibold leading-tight">{activeModule.sidebarTitle}</span>
                    </div>
                  )}

                  {scenes.map((s, i) => {
                    const Icon = sceneIcon(s.navLabel);
                    const selected = i === sceneIndex;
                    return (
                      <button
                        key={`${s.navLabel}-${i}`}
                        type="button"
                        onClick={() => {
                          goToScene(i);
                          setPlaying(true);
                        }}
                        title={s.navLabel}
                        className={`w-full flex items-center gap-1.5 rounded-md px-2 py-1.5 text-left transition-colors border ${
                          selected
                            ? "border-blue-600 bg-blue-50 text-blue-600 font-medium"
                            : "border-transparent text-gray-600 hover:bg-gray-50"
                        }`}
                      >
                        <Icon className="shrink-0 w-4 h-4" />
                        <span className="text-xs leading-snug break-words">{s.navLabel}</span>
                      </button>
                    );
                  })}
                </nav>
              </aside>

              <div className="flex-1 min-h-0 relative overflow-hidden bg-white">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={`${activeModule?.id}-${sceneIndex}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="absolute inset-0"
                  >
                    {scene && <TutorialPageMock scene={scene} />}
                  </motion.div>
                </AnimatePresence>

                {!finished && (
                  <motion.div
                    key={`ptr-${activeModule?.id}-${sceneIndex}`}
                    className="pointer-events-none absolute z-20"
                    initial={{ left: "18%", top: "28%", opacity: 0, scale: 0.85 }}
                    animate={{
                      left: ["18%", "42%", "58%", "48%"],
                      top: ["28%", "38%", "52%", "62%"],
                      opacity: 1,
                      scale: [1, 1.15, 1, 1.15, 1],
                    }}
                    transition={{
                      duration: Math.max(3.5, (scene?.durationSec ?? 5) * 0.85),
                      ease: "easeInOut",
                      repeat: Infinity,
                      repeatType: "mirror",
                    }}
                  >
                    <span className="absolute -top-2.5 -left-2.5 w-9 h-9 rounded-full bg-blue-500/25 blur-[2px]" />
                    <span className="absolute -top-1.5 -left-1.5 w-7 h-7 rounded-full border-2 border-blue-500/50 animate-ping" />
                    <MousePointer2
                      size={30}
                      className="relative text-white drop-shadow-[0_2px_4px_rgba(29,78,216,0.6)]"
                      fill="#1d4ed8"
                      strokeWidth={1.5}
                    />
                  </motion.div>
                )}
              </div>
            </div>
            </div>
          </div>
          </div>
          </div>
        </div>

        {/* Narration panel */}
        <div className="w-full min-w-0">
          <div className="w-full rounded-2xl border dark:border-white/[0.08] border-gray-100 dark:bg-white/[0.02] bg-gray-50/80 p-5">
            <AnimatePresence mode="wait">
              <motion.div
                key={finished ? "done" : `s-${sceneIndex}`}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="flex flex-col w-full min-w-0"
              >
                {finished ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center shrink-0">
                        <PartyPopper className="text-emerald-600" size={22} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-emerald-600 uppercase tracking-wide">Tour complete</p>
                        <h3 className="text-base font-bold dark:text-white text-gray-900">{activeModule?.name}</h3>
                      </div>
                    </div>
                    <p className="text-sm dark:text-slate-400 text-gray-600 leading-relaxed">
                      You&apos;ve finished every page here. Try one live, or switch modules above for the next tour.
                    </p>
                  </div>
                ) : (
                  <div className="flex min-w-0 gap-3">
                    <div className="flex flex-col items-center shrink-0 self-stretch">
                      <div className="relative flex flex-col items-center h-full min-h-[100px]">
                        <div className="relative z-10 w-9 h-9 rounded-full bg-blue-600 text-white text-sm font-bold flex items-center justify-center shrink-0">
                          {sceneIndex + 1}
                        </div>
                        <span
                          aria-hidden
                          className="absolute left-1/2 top-[18px] bottom-[18px] w-0.5 -translate-x-1/2 dark:bg-white/10 bg-gray-200"
                        />
                        <div className="relative z-10 mt-auto w-9 h-9 rounded-full border dark:border-white/10 border-gray-200 dark:bg-vw-surface bg-white text-xs font-bold text-gray-400 flex items-center justify-center shrink-0">
                          {total}
                        </div>
                      </div>
                    </div>

                    <div className="min-w-0 flex-1 space-y-4">
                      <div className="min-w-0">
                        <div className="inline-flex items-center gap-2 mb-2 max-w-full min-w-0">
                          {SceneIcon && <SceneIcon className="text-blue-600 shrink-0 w-5 h-5" />}
                          <span className="text-sm font-semibold text-blue-600 truncate">{scene?.pageTitle}</span>
                        </div>
                        <h3 className="text-base font-bold dark:text-white text-gray-900 leading-snug break-words">
                          {scene?.headline}
                        </h3>
                        <p className="text-sm dark:text-slate-400 text-gray-600 mt-2 leading-relaxed">{scene?.narration}</p>
                      </div>

                      {(scene?.bullets?.length ?? 0) > 0 && (
                        <div className="relative min-w-0">
                          {(scene?.bullets?.length ?? 0) > 1 && (
                            <span
                              aria-hidden
                              className="absolute left-3 top-3 bottom-3 w-0.5 -translate-x-1/2 bg-blue-600/30"
                            />
                          )}
                          <ol className="relative space-y-3">
                            {(scene?.bullets ?? []).map((b, i) => (
                              <li key={b} className="flex gap-3 items-start min-w-0">
                                <span className="relative z-10 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-blue-600/40 dark:bg-vw-surface bg-white text-xs font-bold text-blue-600 shadow-sm">
                                  {i + 1}
                                </span>
                                <p className="text-sm dark:text-slate-300 text-gray-700 pt-0.5">{b}</p>
                              </li>
                            ))}
                          </ol>
                        </div>
                      )}

                      <div className="flex flex-wrap items-center gap-2 pt-1 border-t dark:border-white/[0.08] border-gray-100">
                        <button
                          type="button"
                          onClick={() => {
                            if (sceneIndex > 0) {
                              goToScene(sceneIndex - 1);
                              setPlaying(true);
                            }
                          }}
                          disabled={sceneIndex === 0}
                          className="p-2.5 rounded-xl border dark:border-white/10 border-gray-200 dark:text-slate-400 text-gray-600 hover:border-blue-600 hover:text-blue-600 disabled:opacity-35 transition-colors"
                          aria-label="Previous"
                        >
                          <ChevronLeft size={18} />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (sceneIndex >= total - 1) {
                              setFinished(true);
                              setPlaying(false);
                            } else {
                              goToScene(sceneIndex + 1);
                              setPlaying(true);
                            }
                          }}
                          className="p-2.5 rounded-xl border dark:border-white/10 border-gray-200 dark:text-slate-400 text-gray-600 hover:border-blue-600 hover:text-blue-600 transition-colors"
                          aria-label="Next"
                        >
                          <ChevronRight size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlatformTour;
